/** Polish-locale formatting helpers. */

/** "05:00:00" -> "5:00" (Polish convention: no leading zero on the hour). */
export function formatTime(time: string): string {
  const [hours, minutes] = time.split(":");
  return `${Number(hours)}:${minutes}`;
}

/** ISO datetime -> "sierpień 2026" (for "jeździ od …"). */
export function formatMonthYear(iso: string): string {
  return new Intl.DateTimeFormat("pl-PL", { month: "long", year: "numeric" }).format(
    new Date(iso),
  );
}

/** ISO datetime -> "6:52" for chat timestamps (today) or "wt. 11.08, 6:52". */
export function formatMessageTime(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  const time = new Intl.DateTimeFormat("pl-PL", {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
  if (sameDay) return time;
  const day = new Intl.DateTimeFormat("pl-PL", { weekday: "short", day: "2-digit", month: "2-digit" }).format(date);
  return `${day}, ${time}`;
}

// The crews live on Polish calendar days and the API counts "today" in
// Europe/Warsaw (core/clock.py). A phone on another zone — abroad, or a
// mis-set clock — must not disagree, or it shows the status buttons for the
// wrong day and the API refuses the tap (code review, 2026-10-22).
const WARSAW_DATE = new Intl.DateTimeFormat("en-US", {
  timeZone: "Europe/Warsaw",
  year: "numeric",
  month: "numeric",
  day: "numeric",
});

/** Today's Polish calendar date, as UTC midnight: calendar arithmetic only.
 *  Read field by field — a locale's date *pattern* is not a contract (Chrome
 *  110 briefly turned en-CA "2026-10-22" into "10/22/2026"). */
function warsawToday(): Date {
  // One reading of the clock: three would straddle midnight once a year and
  // pair 31 December's year with 1 January's month and day.
  const parts = WARSAW_DATE.formatToParts(new Date());
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);
  return new Date(Date.UTC(part("year"), part("month") - 1, part("day")));
}

/** Polish calendar date as ISO "YYYY-MM-DD", `offsetDays` from today. */
export function isoDate(offsetDays = 0): string {
  const date = warsawToday();
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return date.toISOString().slice(0, 10);
}

/** ISO date "2026-08-28" -> "cz 28.08" (parsed as a local date, so no off-by-one). */
export function formatShortDate(iso: string): string {
  const [year, month, day] = iso.split("-").map(Number);
  if (year === undefined || month === undefined || day === undefined) return iso;
  const date = new Date(year, month - 1, day);
  // JS: Sunday = 0; our tables are Monday-first.
  const weekday = (date.getDay() + 6) % 7;
  return `${DAY_ABBR[weekday]} ${String(day).padStart(2, "0")}.${String(month).padStart(2, "0")}`;
}

/** Today in Poland as "poniedziałek, 11 sierpnia". */
export function formatToday(): string {
  return new Intl.DateTimeFormat("pl-PL", {
    timeZone: "Europe/Warsaw",
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date());
}

/** "Biała" or "Biała (tucholski)" when town names collide across powiats. */
export function townLabel(area: { name: string; district: string | null }): string {
  return area.district ? `${area.name} (${area.district})` : area.name;
}

/** Today's weekday in Poland, Monday-first like our tables (0 = Monday … 6 = Sunday). */
export function todayWeekday(): number {
  return (warsawToday().getUTCDay() + 6) % 7;
}

/** Polish weekday abbreviations, Monday-first (day 0 = Monday). */
export const DAY_ABBR = ["pn", "wt", "śr", "cz", "pt", "so", "nd"] as const;

/** [0,1,2,3,4] -> "pn–pt"; sparse sets -> "pn, śr, pt". */
export function formatDays(days: number[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  if (first === undefined || last === undefined) return "";
  if (sorted.length > 2 && last - first === sorted.length - 1) {
    return `${DAY_ABBR[first]}–${DAY_ABBR[last]}`;
  }
  return sorted.map((day) => DAY_ABBR[day]).join(", ");
}

export function initials(fullName: string): string {
  return fullName
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

/** Keeps only what a phone number can contain: digits, spaces, "+", "-", "()". */
export function phoneInput(value: string): string {
  return value.replace(/[^\d+()\s-]/g, "");
}
